import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

const normalizeEmail = (email) => (email || '')?.trim()?.toLowerCase();

// Build redirect URLs for Supabase emails.
// If a custom redirect is provided AND we're sending the default dashboard link, use it.
// Otherwise, always prefer current origin + requested path so reset links land on the correct page.
const getRedirectUrl = (path = '/user-dashboard') => {
  const explicitPath = path?.startsWith('/') ? path : `/${path || ''}`;
  const envRedirect = import.meta.env?.VITE_EMAIL_REDIRECT_URL;

  // For non-default paths (e.g., reset-password), force origin + path to avoid
  // being sent to whatever VITE_EMAIL_REDIRECT_URL points to (often dashboard).
  if (explicitPath !== '/user-dashboard') {
    return `${window?.location?.origin}${explicitPath}`;
  }

  // Default path: honor env override if present; else fall back to origin.
  return envRedirect || `${window?.location?.origin}${explicitPath}`;
};

// Retry helper with exponential backoff for network failures
const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000) => {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      const isNetworkError = error?.message?.includes('fetch') || 
                             error?.message?.includes('network') ||
                             error?.message?.includes('Failed to fetch');
      
      const isLastAttempt = attempt === maxRetries - 1;
      
      if (!isNetworkError || isLastAttempt) {
        throw error;
      }
      
      // Exponential backoff: 1s, 2s, 4s
      const delay = baseDelay * Math.pow(2, attempt);
      console.log(`Retrying after ${delay}ms (attempt ${attempt + 1}/${maxRetries})...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connectionError, setConnectionError] = useState(null);

  useEffect(() => {
    // Check active session with retry logic
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await retryWithBackoff(
          () => supabase?.auth?.getSession()
        );
        
        if (error) throw error;
        
        setUser(session?.user ?? null);
        setConnectionError(null);
        
        if (session?.user) {
          await loadProfile(session?.user?.id);
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Error getting session:', error);
        setConnectionError('Unable to connect to authentication service. Please check your internet connection.');
        setLoading(false);
      }
    };
    
    initializeAuth();

    // CRITICAL FIX: Correct Supabase v2 auth state subscription pattern
    // Supabase v2 directly returns { data: { subscription } } with unsubscribe method
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session?.user?.id);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    // Cleanup: call unsubscribe() directly on the subscription object
    return () => {
      subscription?.unsubscribe?.();
    };
  }, []);

  const loadProfile = async (userId) => {
    try {
      const { data, error } = await retryWithBackoff(
        () => supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single()
      );

      if (error) throw error;
      setProfile(data);
      setConnectionError(null);
    } catch (error) {
      console.error('Error loading profile:', error?.message);
      setConnectionError('Unable to load user profile. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    const normalizedEmail = normalizeEmail(email);
    try {
      const { data, error } = await retryWithBackoff(
        () => supabase?.auth?.signInWithPassword({
          email: normalizedEmail,
          password: password?.trim?.() || password
        })
      );
      
      if (error) {
        // CRITICAL FIX: Sanitize error messages to remove OAuth/Google references
        // for email/password authentication context
        let errorMessage = error?.message || 'Authentication failed';
        
        // Remove OAuth provider references from error messages
        errorMessage = errorMessage?.replace(/google/gi, '')?.replace(/oauth/gi, '')?.replace(/provider/gi, '')?.replace(/\s{2,}/g, ' ')?.trim();
        
        // Create clean error object
        const cleanError = new Error(errorMessage || 'Invalid login credentials');
        cleanError.status = error?.status;
        throw cleanError;
      }
      
      setConnectionError(null);
      return data;
    } catch (error) {
      // Check if it's a network error
      if (error?.message?.includes('fetch') || error?.message?.includes('Failed to fetch')) {
        const networkError = new Error('Unable to connect to authentication service. Please check your internet connection and try again.');
        setConnectionError(networkError?.message);
        throw networkError;
      }
      
      // Ensure we're throwing a clean error without OAuth references
      if (error?.message) {
        let cleanMessage = error?.message?.replace(/google/gi, '')?.replace(/oauth/gi, '')?.replace(/provider/gi, '')?.replace(/\s{2,}/g, ' ')?.trim();
        
        const cleanError = new Error(cleanMessage || 'Authentication failed');
        throw cleanError;
      }
      throw error;
    }
  };

  const signUp = async (email, password, metadata) => {
    const normalizedEmail = normalizeEmail(email);
    const trimmedPassword = password?.trim?.() || password;
    
    try {
      const { data, error } = await retryWithBackoff(
        () => supabase?.auth?.signUp({
          email: normalizedEmail,
          password: trimmedPassword,
          options: {
            data: metadata || {
              full_name: '',
              role: 'author'
            },
            emailRedirectTo: getRedirectUrl()
          }
        })
      );
      
      if (error) throw error;
      if (!data?.user && !data?.session) {
        throw new Error('Sign-up did not complete. Please try again.');
      }
      
      // Check if email confirmation is required
      if (data?.user && !data?.session) {
        // User created but needs to confirm email
        console.log('Email confirmation required for:', normalizedEmail);
      }
      
      setConnectionError(null);
      return data;
    } catch (error) {
      if (error?.message?.includes('fetch') || error?.message?.includes('Failed to fetch')) {
        const networkError = new Error('Unable to connect to authentication service. Please check your internet connection and try again.');
        setConnectionError(networkError?.message);
        throw networkError;
      }
      throw error;
    }
  };

  const resendConfirmationEmail = async (email) => {
    const normalizedEmail = normalizeEmail(email);
    try {
      const { data, error } = await retryWithBackoff(
        () => supabase?.auth?.resend({
          type: 'signup',
          email: normalizedEmail,
          options: {
            emailRedirectTo: getRedirectUrl()
          }
        })
      );
      if (error) throw error;
      setConnectionError(null);
      return data;
    } catch (error) {
      if (error?.message?.includes('fetch') || error?.message?.includes('Failed to fetch')) {
        throw new Error('Unable to connect to email service. Please try again later.');
      }
      throw error;
    }
  };

  const sendPasswordReset = async (email) => {
    const normalizedEmail = normalizeEmail(email);
    try {
      const { data, error } = await retryWithBackoff(
        () => supabase?.auth?.resetPasswordForEmail(normalizedEmail, {
          redirectTo: getRedirectUrl('/reset-password')
        })
      );
      if (error) throw error;
      setConnectionError(null);
      return data;
    } catch (error) {
      if (error?.message?.includes('fetch') || error?.message?.includes('Failed to fetch')) {
        throw new Error('Unable to connect to email service. Please try again later.');
      }
      throw error;
    }
  };

  const updatePassword = async (password) => {
    const trimmedPassword = password?.trim?.() || password;
    try {
      const { data, error } = await retryWithBackoff(
        () => supabase?.auth?.updateUser({ password: trimmedPassword })
      );
      if (error) throw error;
      setConnectionError(null);
      return data;
    } catch (error) {
      if (error?.message?.includes('fetch') || error?.message?.includes('Failed to fetch')) {
        throw new Error('Unable to update password. Please check your connection and try again.');
      }
      throw error;
    }
  };

  const signOut = async () => {
    try {
      const { error } = await retryWithBackoff(
        () => supabase?.auth?.signOut()
      );
      if (error) throw error;
      setUser(null);
      setProfile(null);
      setConnectionError(null);
    } catch (error) {
      // Even if signOut fails, clear local state
      setUser(null);
      setProfile(null);
      console.error('Error signing out:', error);
    }
  };

  const signInWithOAuth = async (provider) => {
    try {
      const { data, error } = await retryWithBackoff(
        () => supabase?.auth?.signInWithOAuth({
          provider,
          options: {
            redirectTo: `${window.location?.origin}/user-dashboard`,
            queryParams: {
              access_type: 'offline',
              prompt: 'consent',
            }
          }
        })
      );
      if (error) throw error;
      setConnectionError(null);
      return data;
    } catch (error) {
      if (error?.message?.includes('fetch') || error?.message?.includes('Failed to fetch')) {
        throw new Error('Unable to connect to OAuth service. Please try again later.');
      }
      throw error;
    }
  };

  const updateProfile = async (updates) => {
    if (!user) throw new Error('No user logged in');

    try {
      const { data, error } = await retryWithBackoff(
        () => supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      );

      if (error) throw error;
      setProfile(data);
      setConnectionError(null);
      return data;
    } catch (error) {
      if (error?.message?.includes('fetch') || error?.message?.includes('Failed to fetch')) {
        throw new Error('Unable to update profile. Please check your connection and try again.');
      }
      throw error;
    }
  };

  const value = {
    user,
    profile,
    loading,
    connectionError,
    signIn,
    signUp,
    signOut,
    signInWithOAuth,
    updateProfile,
    resendConfirmationEmail,
    sendPasswordReset,
    updatePassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};